import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, catchError, forkJoin, map, Observable, of, shareReplay, switchMap, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { Book, BookManifest, Chapter, CurriculumManifest, FlashCard, FlashCardDeck, Lesson, Quiz, QuizQuestion, Slide, Subject } from '../models';

@Injectable({
  providedIn: 'root'
})
export class DataService {
  private http = inject(HttpClient);
  private apiUrl = environment.apiUrl || '';

  private curriculumSubject$ = new BehaviorSubject<CurriculumManifest | null>(null);
  private bookChaptersCache = new Map<string, Chapter[]>();

  private imageMap$ = this.http.get<Record<string, string>>('/assets/data/image_map.json').pipe(
    shareReplay(1)
  );

  refreshCurriculum(): Observable<CurriculumManifest> {
    this.bookChaptersCache.clear();
    return this.http
      .get<CurriculumManifest>(`${this.apiUrl}/api/public/curriculum?_t=${Date.now()}`)
      .pipe(
        tap(data => this.curriculumSubject$.next(data)),
        catchError(err => {
          console.warn('API getCurriculum failed, falling back to local asset:', err);
          return this.http.get<CurriculumManifest>('/assets/data/curriculum.json').pipe(
            tap(data => this.curriculumSubject$.next(data))
          );
        })
      );
  }

  getCurriculum(forceRefresh: boolean = false): Observable<CurriculumManifest> {
    if (!forceRefresh && this.curriculumSubject$.value) {
      return this.curriculumSubject$.asObservable().pipe(
        map(c => c!)
      );
    }
    return this.refreshCurriculum();
  }

  getCoursesPageConfig(): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/api/public/curriculum/courses-page-config?_t=${Date.now()}`).pipe(
      catchError(err => {
        console.warn('API getCoursesPageConfig failed, falling back to local asset:', err);
        return this.http.get<any>('/assets/data/courses_page_config.json');
      })
    );
  }

  getSubjects(forceRefresh: boolean = false): Observable<Subject[]> {
    return this.getCurriculum(forceRefresh).pipe(
      map(c => c.subjects || [])
    );
  }

  getBooksBySubject(subjectId: string, forceRefresh: boolean = false): Observable<Book[]> {
    return this.getCurriculum(forceRefresh).pipe(
      map(c => (c.books || []).filter(b => b.subjectId === subjectId))
    );
  }

  getBook(bookId: string, forceRefresh: boolean = false): Observable<Book | undefined> {
    return this.getCurriculum(forceRefresh).pipe(
      map(c => (c.books || []).find(b => b.id === bookId))
    );
  }

  getChapters(bookId: string = 'middle_school_math', forceRefresh: boolean = false): Observable<Chapter[]> {
    const targetBookId = bookId || 'middle_school_math';

    if (!forceRefresh && this.bookChaptersCache.has(targetBookId)) {
      return of(this.bookChaptersCache.get(targetBookId)!);
    }

    return this.http.get<BookManifest>(`${this.apiUrl}/api/public/curriculum/books/${targetBookId}/chapters?_t=${Date.now()}`).pipe(
      map(manifest => manifest.chapters || []),
      tap(chapters => this.bookChaptersCache.set(targetBookId, chapters)),
      catchError(err => {
        console.warn(`API getChapters for ${targetBookId} failed, falling back to local asset:`, err);
        const fallbackFile = targetBookId === 'middle_school_math' ? 'chapters.json' : `${targetBookId}_chapters.json`;
        return this.http.get<BookManifest>(`/assets/data/${fallbackFile}`).pipe(
          map(manifest => manifest.chapters || []),
          tap(chapters => this.bookChaptersCache.set(targetBookId, chapters)),
          catchError(() => of([]))
        );
      })
    );
  }

  private findChapter(chapters: Chapter[], idOrIndex: string | number | null | undefined): { chapter: Chapter; index: number } | null {
    if (!chapters || chapters.length === 0 || idOrIndex == null) return null;
    
    // Check if numeric index
    const num = typeof idOrIndex === 'number' ? idOrIndex : parseInt(idOrIndex, 10);
    if (!isNaN(num) && String(num) === String(idOrIndex) && num >= 0 && num < chapters.length) {
      return { chapter: chapters[num], index: num };
    }
    
    // Check by string ID
    const strId = String(idOrIndex);
    const foundIdx = chapters.findIndex(c => c.id === strId);
    if (foundIdx >= 0) {
      return { chapter: chapters[foundIdx], index: foundIdx };
    }
    
    // Fallback to numeric index if valid
    if (!isNaN(num) && num >= 0 && num < chapters.length) {
      return { chapter: chapters[num], index: num };
    }
    
    return { chapter: chapters[0], index: 0 };
  }

  getChapterByIndexOrId(idOrIndex: string | number, bookId?: string): Observable<{ chapter: Chapter; index: number } | null> {
    return this.getChapters(bookId).pipe(
      map(chapters => this.findChapter(chapters, idOrIndex))
    );
  }

  getChapter(idOrIndex: string | number, bookId?: string): Observable<Chapter | undefined> {
    return this.getChapters(bookId).pipe(
      map(chapters => this.findChapter(chapters, idOrIndex)?.chapter)
    );
  }

  private resolvePath(filename: string, imageMap: Record<string, string>): string {
    const path = imageMap[filename];
    if (!path) {
      console.warn(`Image not found in map: ${filename}`);
      return '/assets/placeholder.png'; // Fallback
    }
    return path.startsWith('/') ? path : `/${path}`;
  }

  getLesson(chapterIndexOrId: number | string, bookId?: string): Observable<Lesson | null> {
    return forkJoin({
      chapters: this.getChapters(bookId),
      imageMap: this.imageMap$
    }).pipe(
      map(({ chapters, imageMap }) => {
        const found = this.findChapter(chapters, chapterIndexOrId);
        if (!found) return null;
        const chapter = found.chapter;

        const slides: Slide[] = (chapter.slides || []).map(slideItem => {
          let imageName = '';
          let audioName = '';

          if (typeof slideItem === 'string') {
            imageName = slideItem;
            audioName = slideItem
              .replace('_slide_', '_audio_')
              .replace(/\.(png|jpg|jpeg)$/, '.mp3');
          } else if (slideItem && typeof slideItem === 'object') {
            imageName = (slideItem as any).image || '';
            audioName = (slideItem as any).audio || imageName
              .replace('_slide_', '_audio_')
              .replace(/\.(png|jpg|jpeg)$/, '.mp3');
          }

          return {
            imagePath: this.resolvePath(imageName, imageMap),
            audioPath: audioName ? this.resolvePath(audioName, imageMap) : undefined
          };
        });

        return { slides };
      })
    );
  }

  getFlashCardDeck(chapterIndexOrId: number | string, bookId?: string): Observable<FlashCardDeck | null> {
    return forkJoin({
      chapters: this.getChapters(bookId),
      imageMap: this.imageMap$
    }).pipe(
      map(({ chapters, imageMap }) => {
        const found = this.findChapter(chapters, chapterIndexOrId);
        if (!found) return null;
        const chapter = found.chapter;

        const cards: FlashCard[] = (chapter.flashCards || []).map(fc => ({
          questionImage: this.resolvePath(fc.question, imageMap),
          answerImage: this.resolvePath(fc.answer, imageMap)
        }));

        return { cards };
      })
    );
  }

  getQuiz(chapterIndexOrId: number | string, bookId?: string): Observable<Quiz | null> {
    return forkJoin({
      chapters: this.getChapters(bookId),
      imageMap: this.imageMap$
    }).pipe(
      map(({ chapters, imageMap }) => {
        const found = this.findChapter(chapters, chapterIndexOrId);
        if (!found) return null;
        const chapter = found.chapter;

        const questions: QuizQuestion[] = (chapter.quizQuestions || []).map(qc => ({
          id: parseInt(qc.id, 10),
          imagePath: this.resolvePath(qc.image, imageMap),
          correctAnswer: qc.correctAnswer,
          numberOfOptions: qc.numberOfOptions
        }));

        const rawScore = parseFloat(chapter.quizPassScorePercentage ?? '80');
        const passingScore = rawScore > 1 ? rawScore / 100 : rawScore;

        return { questions, passingScore };
      })
    );
  }

  // --- Subscription API & Access Control ---

  getCurrentUserEmail(): string {
    try {
      const hasAuthToken = !!localStorage.getItem('AuthToken');
      const hasMwmToken = !!localStorage.getItem('MwmAuthToken');
      const hasUser = !!localStorage.getItem('User');

      // If user is completely logged out, return empty string
      if (!hasAuthToken && !hasMwmToken && !hasUser) {
        return '';
      }

      // 1. Plain string 'User' first if present
      const plainUser = localStorage.getItem('User');
      if (plainUser && plainUser.trim() && plainUser !== 'null' && plainUser !== 'undefined') {
        return plainUser.trim();
      }

      // 2. MwmUser if MwmAuthToken is present
      if (hasMwmToken) {
        const mwmUser = localStorage.getItem('MwmUser');
        if (mwmUser) {
          try {
            const parsed = JSON.parse(mwmUser);
            if (parsed?.email) return parsed.email;
          } catch (_) {}
        }
      }

      // 3. AuthUser / currentUser if AuthToken is present
      if (hasAuthToken) {
        const authUser = localStorage.getItem('AuthUser') || localStorage.getItem('currentUser') || localStorage.getItem('user');
        if (authUser) {
          try {
            const parsed = JSON.parse(authUser);
            if (parsed?.email) return parsed.email;
            if (parsed?.loginId) return parsed.loginId;
          } catch (_) {}
        }
      }
    } catch (e) {
      console.warn('Error reading user email from localStorage:', e);
    }
    return '';
  }

  getSubscriptionPlan(bookId: string): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/api/public/subscription/plans/${bookId}`).pipe(
      catchError(err => {
        console.warn('Failed to fetch subscription plan from backend:', err);
        return of({
          bookId,
          isSubscriptionEnabled: false,
          monthlyPrice: 9.99,
          annualPrice: 79.99,
          currency: 'USD'
        });
      })
    );
  }

  checkSubscriptionStatus(bookId: string, email?: string): Observable<{ hasActiveSubscription: boolean; subscription?: any; isSubscriptionEnabled: boolean }> {
    const userEmail = email || this.getCurrentUserEmail();
    if (!userEmail) {
      return of({ hasActiveSubscription: false, isSubscriptionEnabled: true, hasAccess: false, isSubscribed: false });
    }
    const url = `${this.apiUrl}/api/subscription/status/${bookId}?email=${encodeURIComponent(userEmail)}`;
    return this.http.get<any>(url).pipe(
      map(res => {
        const hasActive = Boolean(res?.hasActiveSubscription || res?.hasAccess || res?.isSubscribed);
        return {
          ...res,
          hasActiveSubscription: hasActive,
          isSubscriptionEnabled: Boolean(res?.isSubscriptionEnabled)
        };
      }),
      catchError(err => {
        console.warn('Failed to check subscription status:', err);
        return of({ hasActiveSubscription: false, isSubscriptionEnabled: false, hasAccess: false, isSubscribed: false });
      })
    );
  }

  createSubscriptionPaymentIntent(bookId: string, planType: 'MONTHLY' | 'ANNUAL', email?: string): Observable<any> {
    const userEmail = email || this.getCurrentUserEmail();
    return this.http.post<any>(`${this.apiUrl}/api/subscription/create-payment-intent`, {
      bookId,
      planType,
      email: userEmail
    });
  }

  confirmSubscription(
    paymentIntentId: string,
    bookId: string,
    planType: 'MONTHLY' | 'ANNUAL',
    email?: string,
    password?: string,
    firstName?: string,
    lastName?: string
  ): Observable<any> {
    const userEmail = email || this.getCurrentUserEmail();
    return this.http.post<any>(`${this.apiUrl}/api/subscription/confirm`, {
      paymentIntentId,
      bookId,
      planType,
      email: userEmail,
      password,
      firstName,
      lastName
    }).pipe(
      tap((res: any) => {
        if (res && res.token) {
          localStorage.setItem('AuthToken', res.token);
          if (res.user?.email) {
            localStorage.setItem('User', res.user.email);
            localStorage.setItem('AuthUser', JSON.stringify(res.user));
          }
        }
      })
    );
  }

  getMySubscriptions(email?: string): Observable<any[]> {
    const userEmail = email || this.getCurrentUserEmail();
    if (!userEmail) {
      return of([]);
    }
    const url = `${this.apiUrl}/api/subscription/my-subscriptions?email=${encodeURIComponent(userEmail)}`;
    return this.http.get<any[]>(url).pipe(
      catchError(err => {
        console.warn('Failed to fetch subscriptions:', err);
        return of([]);
      })
    );
  }

  canAccessChapter(book: Book | undefined, chapter: Chapter | undefined, hasSubscription: boolean): boolean {
    if (!book || !chapter) return true;
    // If book subscription is disabled or chapter is free preview -> Free access
    if (!book.isSubscriptionEnabled || chapter.isFreePreview) {
      return true;
    }
    // Otherwise requires active subscription
    return hasSubscription;
  }
}
